import { useContextFaqs, type ContextFaq } from "@/hooks/useContextFaqs";
import { useDeviceMode } from "@/hooks/useDeviceMode";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface ContextualHelpPanelProps {
  moduleKey: string;
  pageKey?: string;
  actionKey?: string;
  className?: string;
  /**
   * "fixed"  → legacy behavior: floats fixed top-right on desktop, collapsible bar on mobile.
   * "inline" → renders inside normal document flow with shared HelpCard chrome
   *            (matches sibling context cards in a HelpRail).
   */
  variant?: "fixed" | "inline";
}

export default function ContextualHelpPanel({
  moduleKey,
  pageKey,
  actionKey,
  className,
  variant = "fixed",
}: ContextualHelpPanelProps) {
  const { data: faqs, isLoading } = useContextFaqs(moduleKey, pageKey, actionKey);
  const { isMobile } = useDeviceMode();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isLoading || !faqs || faqs.length === 0) return null;

  // Inline variant: shared card chrome that matches WithdrawContextHelp / DepositContextHelp.
  // Used inside HelpRail on Withdraw / Deposit (and any future migrated page).
  if (variant === "inline") {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border/60 bg-card shadow-sm p-5 space-y-4",
          className
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10">
            <HelpCircle className="h-4 w-4 text-primary" />
          </div>
          <h3 className="text-sm font-semibold text-foreground">Help &amp; FAQs</h3>
        </div>
        <FaqAccordion faqs={faqs} />
        <HelpCenterLink />
      </div>
    );
  }

  // Mobile (fixed variant fallback): collapsible section
  if (isMobile) {
    return (
      <div className={cn("mt-6 rounded-2xl border border-border/60 bg-card shadow-sm", className)}>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="flex items-center justify-between w-full px-4 py-3.5 text-sm font-medium text-foreground"
        >
          <span className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10">
              <HelpCircle className="h-4 w-4 text-primary" />
            </div>
            Need Help?
          </span>
          <span className="text-xs text-muted-foreground">
            {mobileOpen ? "Hide" : `${faqs.length} FAQs`}
          </span>
        </button>
        {mobileOpen && (
          <div className="px-4 pb-4">
            <FaqAccordion faqs={faqs} />
            <HelpCenterLink />
          </div>
        )}
      </div>
    );
  }

  // Desktop (fixed variant): floating side panel
  return (
    <div
      className={cn(
        "fixed right-6 top-[5.5rem] w-80 z-20 hidden xl:block max-h-[calc(100vh-6.5rem)] overflow-y-auto scrollbar-thin",
        className
      )}
    >
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-border/40">
          <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10">
            <HelpCircle className="h-4 w-4 text-primary" />
          </div>
          <h3 className="text-sm font-semibold text-foreground">
            Help &amp; FAQs
          </h3>
        </div>
        <FaqAccordion faqs={faqs} />
        <HelpCenterLink />
      </div>
    </div>
  );
}

function FaqAccordion({ faqs }: { faqs: ContextFaq[] }) {
  return (
    <Accordion type="single" collapsible className="w-full space-y-0.5">
      {faqs.map((faq) => (
        <AccordionItem key={faq.id} value={faq.id} className="border-border/30 last:border-b-0">
          <AccordionTrigger className="text-[13px] leading-snug font-medium text-foreground hover:no-underline py-3 gap-3 items-start [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:mt-0.5 [&>svg]:shrink-0 [&>svg]:text-muted-foreground">
            <span className="text-left">{faq.question}</span>
          </AccordionTrigger>
          <AccordionContent className="text-[13px] text-muted-foreground pb-3 pt-0 leading-relaxed">
            {faq.answer}
            {faq.related_link && (
              <Link
                to={faq.related_link}
                className="inline-flex items-center gap-1 text-primary hover:underline mt-2 text-[12px] font-medium"
              >
                Learn more <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function HelpCenterLink() {
  return (
    <Link
      to="/app/help"
      className="flex items-center gap-1.5 text-[12px] font-medium text-primary hover:underline mt-4 pt-3 border-t border-border/40"
    >
      <ExternalLink className="h-3 w-3" />
      View Full Help Center
    </Link>
  );
}

/** Inline collapsible help bar — sits in normal document flow instead of fixed sidebar */
export function InlineHelpPanel({
  moduleKey,
  pageKey,
  actionKey,
  className,
}: ContextualHelpPanelProps) {
  const { data: faqs, isLoading } = useContextFaqs(moduleKey, pageKey, actionKey);
  const [open, setOpen] = useState(false);

  if (isLoading || !faqs || faqs.length === 0) return null;

  return (
    <div className={cn("rounded-xl glass glow-border", className)}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground"
      >
        <span className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10">
            <HelpCircle className="h-4 w-4 text-primary" />
          </div>
          Help & FAQs
        </span>
        <span className="text-xs text-muted-foreground">
          {open ? "Hide" : `${faqs.length} FAQs`}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <FaqAccordion faqs={faqs} />
          <HelpCenterLink />
        </div>
      )}
    </div>
  );
}

/** Wrapper to add help panel alongside page content */
export function HelpPanelLayout({
  moduleKey,
  pageKey,
  actionKey,
  children,
}: {
  moduleKey: string;
  pageKey?: string;
  actionKey?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-6 items-start">
      <div className="flex-1 min-w-0">{children}</div>
      <ContextualHelpPanel
        moduleKey={moduleKey}
        pageKey={pageKey}
        actionKey={actionKey}
      />
    </div>
  );
}
